/**
 * TvaReduiteGuide
 * ─────────────────────────────────────────────────────────────────
 * Guide éditorial complet pour /particuliers/aides/tva-reduite.
 *
 * Structure :
 *  1. Hero (eyebrow + titre + lead + sidecard 14,5 points)
 *  2. Bandeau "En résumé"
 *  3. Mécanisme (article 278-0 bis A)
 *  4. Comparatif des 3 taux (5,5 / 10 / 20 %)
 *  5. Travaux éligibles (3 catégories)
 *  6. Conditions (4 cartes)
 *  7. Pièges à éviter
 *  8. Cumul avec autres aides
 *  9. FAQ
 * 10. CTA final
 *
 * Contenu : `client/src/config/tva-reduite-content.ts`.
 */

import { ArrowRight, CheckCircle2, Info, ShieldAlert } from 'lucide-react';

import { Container } from '../layout/Container';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { ArticleLd } from '../seo/jsonld/Article';
import { BreadcrumbListLd } from '../seo/jsonld/BreadcrumbList';
import { FAQPageLd } from '../seo/jsonld/FAQPage';
import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';
import {
  CTA_HREF,
  SectionHeader,
  SITE_URL,
} from './_shared';
import {
  PARTICULIER_URLS,
  ParticulierCTAFinal,
  ParticulierLiveCard,
  ParticulierPageHero,
} from '../audience/particulier';
import {
  TVA_CONDITIONS,
  TVA_CUMULS,
  TVA_FAQ,
  TVA_HERO,
  TVA_MECANISME,
  TVA_PIEGES,
  TVA_SUMMARY,
  TVA_TAUX,
  TVA_TRAVAUX,
} from '../../config/tva-reduite-content';

const PAGE_URL = `${SITE_URL}/particuliers/aides/tva-reduite`;

const CUMUL_TONE_STYLES: Record<
  'brand' | 'accent' | 'sage' | 'lavender' | 'ivory',
  string
> = {
  brand: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  accent: 'bg-accent-50 text-accent-800 ring-accent-200/60',
  sage: 'bg-stone-100 text-stone-700 ring-stone-200/60',
  lavender: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  ivory: 'bg-ivory-100 text-stone-700 ring-stone-200/60',
};

const TAUX_COLOR_STYLES: Record<'success' | 'warning' | 'error', string> = {
  success: 'bg-brand-50 text-brand-800 ring-brand-200/70',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200/70',
  error: 'bg-rose-50 text-rose-800 ring-rose-200/70',
};

const TAUX_HEADER_STYLES: Record<'success' | 'warning' | 'error', string> = {
  success: 'from-brand-600 to-brand-700 text-white',
  warning: 'from-amber-500 to-amber-600 text-white',
  error: 'from-stone-700 to-stone-800 text-white',
};

// ════════════════════════════════════════════════════════════════
// Composant principal
// ════════════════════════════════════════════════════════════════

export function TvaReduiteGuide() {
  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${SITE_URL}/` },
          { name: 'Particuliers', url: `${SITE_URL}/particuliers` },
          { name: 'Aides', url: `${SITE_URL}/particuliers/aides` },
          { name: 'TVA à 5,5 %', url: PAGE_URL },
        ]}
      />
      <ArticleLd
        headline="TVA à 5,5 % sur les travaux énergétiques 2026"
        url={PAGE_URL}
        datePublished="2026-01-01"
        dateModified={new Date().toISOString().slice(0, 10)}
      />
      <FAQPageLd
        items={TVA_FAQ.map((item) => ({
          question: item.question,
          answer: item.answer,
        }))}
      />

      <HeroSection />
      <SummarySection />
      <MecanismeSection />
      <TauxComparatifSection />
      <TravauxSection />
      <ConditionsSection />
      <PiegesSection />
      <CumulSection />
      <FaqSection />
      <FinalCtaSection />
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// 1. HERO
// ════════════════════════════════════════════════════════════════

function HeroSection() {
  return (
    <ParticulierPageHero
      breadcrumb={[
        { name: 'Aides', href: '/particuliers/aides' },
        { name: 'TVA à 5,5 %' },
      ]}
      eyebrow={TVA_HERO.eyebrow}
      headline={TVA_HERO.titlePrimary}
      headlineAccent={TVA_HERO.titleAccent}
      subheadline={TVA_HERO.lead}
      primaryCta={{ label: 'Estimer l\u2019économie totale', href: CTA_HREF }}
      secondaryCta={{ label: 'Comparer les 3 taux de TVA', href: '#taux' }}
      microPromise={TVA_HERO.microPromise}
      sideSlot={
        <ParticulierLiveCard eyebrow="Économie sur facture HT">
          <p className="bg-gradient-to-br from-brand-700 via-brand-600 to-accent-600 bg-clip-text text-5xl font-black leading-none tracking-tight text-transparent">
            {TVA_HERO.amountHeadline}
          </p>
          <p className="mt-3 text-[13px] leading-relaxed text-stone-500">
            {TVA_HERO.amountSubline}
          </p>
          <div className="mt-5 border-t border-stone-100 pt-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400">
              Comparaison rapide
            </p>
            <ul className="mt-3 space-y-2 text-[13px] text-stone-700">
              <li className="flex items-center justify-between">
                <span>10 000 € HT à 20 %</span>
                <span className="font-semibold text-stone-500">12 000 € TTC</span>
              </li>
              <li className="flex items-center justify-between">
                <span>10 000 € HT à 5,5 %</span>
                <span className="font-semibold text-brand-700">10 550 € TTC</span>
              </li>
              <li className="flex items-center justify-between border-t border-stone-100 pt-2">
                <span className="font-semibold">Économie</span>
                <span className="font-bold text-brand-600">1 450 €</span>
              </li>
            </ul>
          </div>
        </ParticulierLiveCard>
      }
    />
  );
}

// ════════════════════════════════════════════════════════════════
// 2. EN RÉSUMÉ
// ════════════════════════════════════════════════════════════════

function SummarySection() {
  return (
    <section className="border-y border-stone-200/70 bg-stone-50/60 py-10 md:py-14">
      <Container>
        <div className="rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle md:p-8">
          <div className="mb-5 flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
              <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
            </span>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-500">
              En résumé
            </p>
          </div>
          <ul className="grid gap-3 md:grid-cols-2">
            {TVA_SUMMARY.map((point) => (
              <li
                key={point.text}
                className="flex items-start gap-2.5 rounded-xl border border-stone-100 bg-stone-50/50 p-3.5"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" strokeWidth={2.4} />
                <span className="text-[13.5px] leading-relaxed text-stone-700">{point.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 3. MÉCANISME
// ════════════════════════════════════════════════════════════════

function MecanismeSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow={TVA_MECANISME.eyebrow}
          title={TVA_MECANISME.title}
          subtitle={TVA_MECANISME.description}
        />
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 4. COMPARATIF DES 3 TAUX
// ════════════════════════════════════════════════════════════════

function TauxComparatifSection() {
  return (
    <section id="taux" className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Les 3 taux de TVA"
          title="Quel taux s’applique à vos travaux ?"
          subtitle="Les trois taux de TVA cohabitent sur les logements de plus de 2 ans. C’est la nature du travail réalisé qui détermine le taux applicable."
        />

        <div className="grid gap-5 md:grid-cols-3">
          {TVA_TAUX.map((taux) => (
            <article
              key={taux.taux}
              className="flex flex-col overflow-hidden rounded-2xl border border-stone-200 bg-paper shadow-subtle"
            >
              <header
                className={cn(
                  'bg-gradient-to-br px-5 py-5',
                  TAUX_HEADER_STYLES[taux.color],
                )}
              >
                <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] opacity-80">
                  {taux.label}
                </p>
                <p className="mt-2 font-display text-4xl font-black leading-none tracking-tight">
                  {taux.taux}
                </p>
              </header>
              <div className="flex flex-1 flex-col gap-3 p-5">
                <p className="text-[13px] leading-relaxed text-stone-600">{taux.description}</p>
                <p
                  className={cn(
                    'mt-1 inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset',
                    TAUX_COLOR_STYLES[taux.color],
                  )}
                >
                  Exemples
                </p>
                <ul className="space-y-1.5">
                  {taux.examples.map((ex) => (
                    <li
                      key={ex}
                      className="flex items-start gap-2 text-[12.5px] leading-relaxed text-stone-700"
                    >
                      <CheckCircle2
                        className="mt-1 h-3.5 w-3.5 shrink-0 text-stone-400"
                        strokeWidth={2.2}
                      />
                      <span>{ex}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 5. TRAVAUX ÉLIGIBLES
// ════════════════════════════════════════════════════════════════

function TravauxSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Travaux éligibles au 5,5 %"
          title="3 grandes familles de travaux énergétiques"
          subtitle="La liste exhaustive est fixée par l’article 278-0 bis A du CGI. Voici les catégories principales rencontrées dans la rénovation résidentielle."
        />

        <div className="grid gap-5 md:grid-cols-3">
          {TVA_TRAVAUX.map((cat) => {
            const Icon = getIcon(cat.iconName);
            return (
              <article
                key={cat.title}
                className="flex flex-col gap-4 rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                  <Icon className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <h3 className="font-display text-lg font-bold text-ink">{cat.title}</h3>
                <ul className="space-y-2">
                  {cat.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-[13px] leading-relaxed text-stone-700"
                    >
                      <CheckCircle2
                        className="mt-1 h-3.5 w-3.5 shrink-0 text-brand-500"
                        strokeWidth={2.2}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 6. CONDITIONS
// ════════════════════════════════════════════════════════════════

function ConditionsSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Conditions"
          title="4 conditions à respecter"
          subtitle="Pas de plafond de ressources, pas d’obligation RGE — mais 4 critères techniques et administratifs à vérifier."
        />

        <div className="grid gap-4 sm:grid-cols-2">
          {TVA_CONDITIONS.map((c) => {
            const Icon = getIcon(c.iconName);
            return (
              <article
                key={c.title}
                className="rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                  <Icon className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <p className="mt-3 font-display text-base font-bold text-ink">{c.title}</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-stone-600">{c.description}</p>
              </article>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 7. PIÈGES À ÉVITER
// ════════════════════════════════════════════════════════════════

function PiegesSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Pièges fréquents"
          title="Les erreurs à éviter pour ne pas perdre le bénéfice du taux réduit"
          subtitle="Trois pièges courants entraînent l’application de la TVA à 20 % ou 10 % au lieu du 5,5 %. Vérifiez chaque point avant de signer."
        />

        <div className="grid gap-4 md:grid-cols-3">
          {TVA_PIEGES.map((p) => {
            const Icon = getIcon(p.iconName);
            return (
              <article
                key={p.title}
                className="rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/70">
                  <Icon className="h-5 w-5" strokeWidth={1.7} />
                </span>
                <p className="mt-3 font-display text-base font-bold text-ink">{p.title}</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-stone-600">{p.description}</p>
              </article>
            );
          })}
        </div>

        <p className="mt-6 flex items-start gap-2.5 rounded-xl bg-amber-50/70 p-4 text-[12.5px] leading-relaxed text-amber-900 ring-1 ring-inset ring-amber-200/70">
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          Conservez la copie de l’attestation et la facture pendant au moins 5 ans. L’administration
          fiscale peut demander ces pièces en cas de contrôle.
        </p>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 8. CUMUL
// ════════════════════════════════════════════════════════════════

function CumulSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Cumul avec les autres aides"
          title="La TVA 5,5 % s’ajoute à toutes les aides"
          subtitle="Aucune restriction de cumul : la TVA réduite est compatible avec MaPrimeRénov’, la prime énergie, l’éco-PTZ et le Coup de pouce."
        />

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {TVA_CUMULS.map((c) => {
            const Icon = getIcon(c.iconName);
            const Tag = c.href ? 'a' : 'div';
            return (
              <Tag
                key={c.aide}
                {...(c.href ? { href: c.href } : {})}
                className={cn(
                  'group flex flex-col gap-3 rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle',
                  c.href && 'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-medium',
                )}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      'inline-flex h-9 w-9 items-center justify-center rounded-lg ring-1 ring-inset',
                      CUMUL_TONE_STYLES[c.tone],
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </span>
                  {c.href ? (
                    <ArrowRight
                      className="h-3.5 w-3.5 text-stone-400 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-brand-600"
                      strokeWidth={2.4}
                    />
                  ) : null}
                </div>
                <p className="text-[14px] font-semibold text-ink">{c.aide}</p>
                <p className="text-[12.5px] leading-relaxed text-stone-600">{c.description}</p>
              </Tag>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 9. FAQ
// ════════════════════════════════════════════════════════════════

function FaqSection() {
  return (
    <section className="py-14 md:py-20">
      <Container size="narrow">
        <SectionHeader
          eyebrow="FAQ"
          title="Vos questions sur la TVA à 5,5 %"
          subtitle="Les questions les plus fréquentes posées par les propriétaires en 2026."
          align="center"
        />

        <div className="overflow-hidden rounded-2xl border border-stone-200 bg-paper shadow-subtle">
          <Accordion type="single" collapsible className="divide-y divide-stone-100">
            {TVA_FAQ.map((item, idx) => (
              <AccordionItem
                key={item.question}
                value={`tva-faq-${idx}`}
                className="border-0 px-5"
              >
                <AccordionTrigger className="text-[15px]">{item.question}</AccordionTrigger>
                <AccordionContent className="text-[14px] leading-relaxed text-stone-600">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <p className="mt-6 flex items-start gap-2.5 text-center text-[12.5px] leading-relaxed text-stone-500">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
          Source : Bulletin officiel des finances publiques (BOI-TVA-LIQ-30-20) — article
          278-0 bis A du Code général des impôts.
        </p>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 10. CTA FINAL
// ════════════════════════════════════════════════════════════════

function FinalCtaSection() {
  return (
    <ParticulierCTAFinal
      badge="Économie totale chiffrée"
      title="Combien économisez-vous au total sur vos travaux ?"
      description={
        <>
          Notre calculateur combine TVA 5,5 % + MaPrimeRénov&rsquo; + prime énergie + éco-PTZ
          pour vous présenter le coût réel de vos travaux après aides. Sans inscription,
          sans engagement.
        </>
      }
      primaryCta={{ label: 'Calculer mon coût net', href: CTA_HREF }}
      secondaryCta={{ label: 'Parler à un conseiller', href: PARTICULIER_URLS.contact }}
    />
  );
}
